/* eslint-disable no-console */
import { HttpResponseStatusCodes } from '@hobt/constants';
import { ApiClient, ApiClientConfig } from 'Foundation/Api';
import { AuthenticationContextType } from 'Foundation/Authentication';
import { config } from '../../../../../config';
import { MasterPoolPremiumsAndTaxes } from '../HbtMasterPoolPremiumsAndTaxes/types';
import {
  LenderOptionType,
  MasterPoolDetails,
  MasterPoolLoanComposition,
  MasterPoolTableFields,
  MasterPoolWorkflowResponse
} from './types';
import { Remark } from '../HbtMasterPoolDetails/types';

export const HTTP_STATUS_ACCEPTED = 202;
export const HTTP_STATUS_CONFLICT = 409;

const isWorkflowSuccessStatus = (statusCode?: number): boolean =>
  statusCode === HttpResponseStatusCodes.Created ||
  statusCode === HttpResponseStatusCodes.OK ||
  statusCode === HTTP_STATUS_ACCEPTED;

type RequestProps = {
  authenticationContext: AuthenticationContextType;
  queryParams: string;
};
const apiClientConfig: ApiClientConfig = {
  timeout: config.validationMasterPoolAPi.requestTimeout
};
const inventoryTimeout: number = config.validationMasterPoolAPi.requestTimeout;
const masterPoolInventoryDataUrl: string = config.validationMasterPoolAPi.urls.masterPoolInventory;
const insuredApprovedLenderListUrl: string =
  config.validationMasterPoolAPi.urls.insuredApprovedLenderList;

const masterPoolDetailsUrl: string = config.validationMasterPoolAPi.urls.masterPoolInventoryDetail;
const masterPoolLoansCompositionUrl: string =
  config.validationMasterPoolAPi.urls.masterPoolLoansComposition;

const rethrowWorkflowError = (err: unknown, context: string): never => {
  if (err instanceof Error) {
    err.message = `${context}: ${err.message}`;
    throw err;
  }

  if (err && typeof err === 'object') {
    const wrappedError = new Error(context);
    (wrappedError as unknown as Record<string, unknown>).originalError = err;
    throw wrappedError;
  }

  throw new Error(`${context}: ${String(err)}`);
};

export const getValidationInventoryTableData = async ({
  authenticationContext,
  queryParams
}: RequestProps): Promise<MasterPoolTableFields[]> => {
  try {
    const masterPoolInventoryUrl = masterPoolInventoryDataUrl + queryParams;
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      masterPoolInventoryUrl,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      []
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error getting master pool table data');
  }
};

export const getInsuredApprovedLenderList = async (
  authenticationContext: AuthenticationContextType
): Promise<LenderOptionType[] | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      insuredApprovedLenderListUrl,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]?.lenders) ||
      null
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error getting table filter column data');
  }
};

export const getMasterPoolDetail = async (
  masterPoolAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolDetails[] | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      `${masterPoolDetailsUrl}/${masterPoolAccountNumber}/details`,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      null
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error getting Master Pool Details');
  }
};

export const getMasterPoolLoansAndCompositions = async (
  masterPoolAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolLoanComposition[] | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      `${masterPoolLoansCompositionUrl}/${masterPoolAccountNumber}/loans/compositions`,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      null
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error getting Master Pool Details');
  }
};

export const getMasterPoolPreimumBreakdown = async (
  masterPoolAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolPremiumsAndTaxes[] | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      `${masterPoolDetailsUrl}/${masterPoolAccountNumber}/premiums`,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data) ||
      null
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error getting Master Pool Premium and Taxes breakdown');
  }
};

export const patchMasterPoolOverrideOfferDate = async (
  cmhcAccountNumber: string,
  date: string,
  authenticationContext: AuthenticationContextType
): Promise<any | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/override-offer-date`,
      { date },
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error overriding offer date');
  }
};
export const patchMasterOverrideApprovalDate = async (
  cmhcAccountNumber: string,
  date: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/override-approval-date`,
      { date },
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error overriding approval date');
  }
};

export const patchMasterOverrideApprove = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/approve`,
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error approving master pool');
  }
};
export const patchMasterOverrideFinalize = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/finalize`,
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error finalizing master pool');
  }
};

export const patchReverseApproval = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/reverse-approval`,
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error reversing approval');
  }
};

export const patchWithdrawMasterPool = async (
  cmhcAccountNumber: string,
  remark: Remark | undefined | null,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const url = `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/withdraw`;

    const body = {
      remark
    };

    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      url,
      body,
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error withdrawing master pool');
  }
};
export const patchReverseFinalizationMasterPool = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const url = `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/reverse-finalization`;

    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(url, {
      timeout: inventoryTimeout
    });
    const statusCode = response.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error reversing finalization');
  }
};

export const patchReverseWithdrawalMasterPool = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const url = `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/reverse-withdrawal`;

    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(url, {
      timeout: inventoryTimeout
    });
    const statusCode = response.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error reversing withdrawal');
  }
};

export const patchMasterPoolOverrideCitadalDate = async (
  cmhcAccountNumber: string,
  date: string,
  authenticationContext: AuthenticationContextType
): Promise<any | null> => {
  try {
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      `${masterPoolDetailsUrl}/${cmhcAccountNumber}/workflows/fraud-detection`,
      { date },
      { timeout: inventoryTimeout }
    );

    const statusCode = response?.data?.statusCode;

    if (isWorkflowSuccessStatus(statusCode)) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error overriding fraud detection date');
  }
};

export const patchReRunPricingScenario = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const url = `${masterPoolDetailsUrl}/${cmhcAccountNumber}/rerun-risking-scenario`;

    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(url, {
      timeout: inventoryTimeout
    });
    const statusCode = response.data?.statusCode;

    if (
      statusCode === HttpResponseStatusCodes.Created ||
      statusCode === HttpResponseStatusCodes.OK
    ) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error while running re-run pricing scenario');
  }
};

export const patchMasterPoolReleaseStatus = async (
  cmhcAccountNumber: string,
  authenticationContext: AuthenticationContextType
): Promise<MasterPoolWorkflowResponse | null> => {
  try {
    const url = `${masterPoolDetailsUrl}/${cmhcAccountNumber}/release-status`;
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(url, {
      timeout: inventoryTimeout
    });
    const statusCode = response?.data?.statusCode;

    if (
      statusCode === HttpResponseStatusCodes.Created ||
      statusCode === HttpResponseStatusCodes.OK
    ) {
      return response?.data?.data || null;
    }

    return null;
  } catch (err) {
    return rethrowWorkflowError(err, 'Error resetting workflow state');
  }
};
